"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useHubs = void 0;
const react_1 = require("react");
const useLogger_1 = require("./useLogger");
const mutations_1 = require("../graphql/mutations");
const queries_1 = require("../graphql/queries");
const subscriptions_1 = require("../graphql/subscriptions");
const amplifyBootstrap_1 = require("../utils/amplifyBootstrap");
const useHubs = (params) => {
    const logger = (0, useLogger_1.useLogger)('useHubs');
    const [hubs, setHubs] = (0, react_1.useState)([]);
    const [hub, setHub] = (0, react_1.useState)(null);
    const [loading, setLoading] = (0, react_1.useState)(false);
    const [updating, setUpdating] = (0, react_1.useState)(false);
    const [error, setError] = (0, react_1.useState)(null);
    const subscriptionsRef = (0, react_1.useRef)([]);
    const fetchAndSubscribeOnLoad = params?.fetchAndSubscribeOnLoad ?? false;
    const fetchHubs = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await amplifyBootstrap_1.API.graphql({
                query: queries_1.listHubs,
                variables: { limit: 100 },
            });
            if (response && response.data) {
                let hubsList = [];
                if (Array.isArray(response.data.listHubs)) {
                    hubsList = response.data.listHubs;
                }
                else if (response.data.listHubs && Array.isArray(response.data.listHubs.items)) {
                    hubsList = response.data.listHubs.items;
                }
                else if (response.data.listHubs) {
                    hubsList = [response.data.listHubs];
                }
                setHubs(hubsList);
            }
            else {
                logger.warn('Unexpected API response structure:', response);
                setHubs([]);
            }
        }
        catch (error) {
            logger.error('Error fetching hubs:', { error });
            setError(error);
            setHubs([]);
        }
        finally {
            setLoading(false);
        }
    };
    const getHubById = async (id) => {
        try {
            setLoading(true);
            setError(null);
            const response = await amplifyBootstrap_1.API.graphql({
                query: queries_1.getHub,
                variables: { id },
            });
            const hubData = response.data.getHub;
            setHub(hubData);
            return hubData;
        }
        catch (error) {
            logger.error('Error fetching hub:', { error });
            setError(error);
            throw error;
        }
        finally {
            setLoading(false);
        }
    };
    const addHub = async (input) => {
        try {
            const response = await amplifyBootstrap_1.API.graphql({
                query: mutations_1.createHub,
                variables: { input },
            });
            return response.data.createHub;
        }
        catch (error) {
            logger.error('Error creating hub:', { error });
            throw error;
        }
    };
    const modifyHub = async (input) => {
        try {
            setUpdating(true);
            const response = await amplifyBootstrap_1.API.graphql({
                query: mutations_1.updateHub,
                variables: { input },
            });
            return response.data.updateHub;
        }
        catch (error) {
            logger.error('Error updating hub:', { error });
            throw error;
        }
        finally {
            setUpdating(false);
        }
    };
    const updateHubAddress = async (input) => {
        try {
            setUpdating(true);
            const response = await amplifyBootstrap_1.API.graphql({
                query: mutations_1.updateHubAddress,
                variables: { input },
            });
            return response.data.updateHubAddress;
        }
        catch (error) {
            logger.error('Error updating hub address:', { error });
            throw error;
        }
        finally {
            setUpdating(false);
        }
    };
    const removeHub = async (id) => {
        try {
            const response = await amplifyBootstrap_1.API.graphql({
                query: mutations_1.deleteHub,
                variables: { id },
            });
            return response.data.deleteHub;
        }
        catch (error) {
            logger.error('Error deleting hub:', { error });
            throw error;
        }
    };
    const clear = () => {
        setHubs([]);
    };
    const fetchAndSubscribe = () => {
        fetchHubs();
        try {
            const createSubscription = amplifyBootstrap_1.API.graphql({
                query: subscriptions_1.onCreateHub,
            }).subscribe({
                next: ({ data }) => {
                    const newHub = data.onHubCreated;
                    if (newHub) {
                        setHubs((prevHubs) => [...prevHubs, newHub]);
                    }
                },
                error: (error) => logger.error('Create subscription error:', error),
            });
            const updateSubscription = amplifyBootstrap_1.API.graphql({
                query: subscriptions_1.onUpdateHub,
            }).subscribe({
                next: ({ data }) => {
                    const updatedHub = data.onHubUpdated;
                    if (updatedHub) {
                        setHubs((prevHubs) => prevHubs.map((hub) => (hub.id === updatedHub.id ? updatedHub : hub)));
                    }
                },
                error: (error) => logger.error('Update subscription error:', error),
            });
            const deleteSubscription = amplifyBootstrap_1.API.graphql({
                query: subscriptions_1.onDeleteHub,
            }).subscribe({
                next: ({ data }) => {
                    const deletedHub = data.onHubDeleted;
                    if (deletedHub) {
                        setHubs((prevHubs) => prevHubs.filter((hub) => hub.id !== deletedHub.id));
                    }
                },
                error: (error) => logger.error('Delete subscription error:', error),
            });
            subscriptionsRef.current = [createSubscription, updateSubscription, deleteSubscription];
        }
        catch (error) {
            logger.error('Error setting up subscriptions:', { error });
        }
    };
    const unsubscribe = () => {
        if (subscriptionsRef.current.length === 0) {
            return;
        }
        try {
            subscriptionsRef.current.forEach((subscription) => {
                if (subscription && typeof subscription.unsubscribe === 'function') {
                    subscription.unsubscribe();
                }
            });
        }
        catch (error) {
            logger.error('Error cleaning up subscriptions:', { error });
        }
    };
    (0, react_1.useEffect)(() => {
        unsubscribe();
        if (fetchAndSubscribeOnLoad) {
            fetchAndSubscribe();
        }
        return () => {
            unsubscribe();
        };
    }, []);
    const getHubConfig = async (id) => {
        try {
            const hub = await getHubById(id);
            if (hub && hub.config) {
                // Assuming hub.config is a JSON string
                return JSON.parse(hub.config);
            }
            else {
                return {};
            }
        }
        catch (error) {
            logger.error('Error fetching hub config:', { error });
            throw error;
        }
    };
    const updateHubConfigData = async (id, configData) => {
        try {
            const configStr = JSON.stringify(configData);
            const response = await amplifyBootstrap_1.API.graphql({
                query: mutations_1.updateHubConfig,
                variables: { id, config: configStr },
            });
            return response.data.updateHubConfig;
        }
        catch (error) {
            logger.error('Error updating hub config:', { error });
            throw error;
        }
    };
    return {
        hubs,
        hub,
        loading,
        updating,
        error,
        clear,
        fetchHubs,
        getHubById,
        addHub,
        modifyHub,
        updateHubAddress,
        removeHub,
        getHubConfig,
        updateHubConfigData,
    };
};
exports.useHubs = useHubs;
